import {Dialog, DialogContent, IconButton, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {Trash2, X} from 'lucide-react';
import {deletePinCode, savePinCode} from '../../../lib/redux/actions';
import useLoader from '../../../hooks/useLoader';
import toast from '../../../lib/utils/toast';

function ManageLocationModel({isOpen, setIsOpen,pinCodes,fetchAllPinCode}) {
  const [locations, setLocations] = useState(['']);
  const [initialLocations, setInitialLocations] = useState([]); // Store initial state
  const [errors, setErrors] = useState([]);
  const [error, setError] = useState('');
  const {showLoader,hideLoader} = useLoader();

  useEffect(()=>{
    const pinCodesArray = extractPincodes(pinCodes);
    if(pinCodesArray.length===0){
      setLocations([""]);
      setInitialLocations([""]);
    }else{
      setLocations(pinCodesArray);
      setInitialLocations(pinCodesArray);
    }
    setError("");
    setErrors([]);
  },[pinCodes])
  
  function extractPincodes(dataArray) {
    return dataArray.map(item => item.pincode);
  }

  // Check if locations have changed from initial state
  const hasChanges = () => {
    if (locations.length !== initialLocations.length) return true;
    
    for (let i = 0; i < locations.length; i++) {
      if (locations[i] !== initialLocations[i]) return true;
    }
    
    return false;
  };

  // Check if all locations are empty strings
  const allEmpty = () => {
    return locations.every(loc => loc.trim() === '');
  };

  const addLocation = () => {
    if (locations.length >= 15) {
      setError('Error: You can only add up to 15 zip codes.');
      return;
    }
    setLocations([...locations, '']);
    setErrors([...errors, '']);
    setError('');
  };

  const handleChange = (index, value) => {
    const newLocations = [...locations];
    newLocations[index] = value.trim();
    setLocations(newLocations);

    const newErrors = [...errors];
    newErrors[index] = '';

    // Check for duplicate
    if (
      value.trim() !== '' &&
      newLocations.filter((loc, i) => loc === value.trim() && i !== index)
        .length > 0
    ) {
      newErrors[index] = 'Duplicate zip code not allowed';
    }

    setErrors(newErrors);
  };

  const removeLocation = async( index) => {
    const locationToRemove = locations[index];

    if(!locationToRemove){
      let newLocations = locations.filter((_, i) => i !== index);
      let newErrors = errors.filter((_, i) => i !== index);

      if (newLocations?.length === 0) {
        newLocations = [""];
        newErrors = [""];
      }
      setLocations(newLocations);


      setErrors(newErrors);
      setError('');
      return;
    }
      const existsInPinCodes = pinCodes.some(p => p.pincode === locationToRemove);

      if (!existsInPinCodes) {
        let newLocations = locations.filter((_, i) => i !== index);
        let newErrors = errors.filter((_, i) => i !== index);
        if (newLocations?.length === 0) {
          newLocations = [""];
          newErrors = [""];
        }
        setLocations(newLocations);

        setErrors(newErrors);
        setError('');
        return;
      }
    try {
      showLoader();
      await deletePinCode(locationToRemove);

       let newLocations = locations.filter((_, i) => i !== index);
       let newErrors = errors.filter((_, i) => i !== index);
      setLocations(newLocations);

     

      if (newLocations?.length === 0) {
        newLocations = [""];
        newErrors = [""];
      }
      setErrors(newErrors);
      setError('');
      toast.success("Pincode deleted successfully")
      fetchAllPinCode();
    } catch (error) {
      toast.error(error?.message)
    }finally{
      hideLoader()
    }
  };

  const handleSubmit = async () => {
    let newErrors = locations.map(loc =>
      loc.trim() === '' ? 'Field is required' : '',
    );

    // Duplicate check on submit
    locations.forEach((loc, i) => {
      if (
        loc.trim() !== '' &&
        locations.filter((item, j) => item === loc && j !== i).length > 0
      ) {
        newErrors[i] = 'Duplicate zip code not allowed';
      }
    });

    setErrors(newErrors);

    // if no errors, proceed
    if (newErrors.every(err => err === '')) {
      const payload = {
        pincodes: locations,
      };
      try {
        showLoader()
        const res = await savePinCode(payload);
        const data = res?.data

         let updatedErrors = [...newErrors];
          // Mark invalid ones
          data.invalid.forEach(invalidCode => {
            locations.forEach((loc, i) => {
              if (loc === invalidCode) {
                updatedErrors[i] = 'Invalid pincode';
              }
            });
          });

      if (data.invalid.length === 0) {
        fetchAllPinCode()
        toast.success("Pincodes saved successfully!")
        setIsOpen(false);
        // Update initial locations after successful save
        setInitialLocations(locations);
      }
      setErrors(updatedErrors);

      } catch (error) {
        toast.error(error);
      }finally{
        hideLoader()
      }
    }
  };

  const anyEmpty = locations.some(loc => loc.trim() === '');
  const shouldDisable = !hasChanges() || allEmpty() || anyEmpty;
  
  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      fullWidth
      PaperProps={{className: 'rounded-[16px]'}}>
      <DialogContent
        className="flex flex-col gap-6 p-8 max-h-[90vh] overflow-y-auto"
        onInteractOutside={e => e.preventDefault()}>
        <IconButton
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4"
          size="small">
          <X className="w-5 h-5" />
        </IconButton>

        <div className="text-left">
          <Typography
            variant="h5"
            className="font-hel font-normal text-[24px] leading-[34px]">
            Manage Location for Weather Forecast
          </Typography>
          <Typography className="mt-1 text-[#000000BF] font-hel font-normal text-sm leading-[20px]">
            Enter your location to get 10-day severe weather alerts.
          </Typography>
        </div>

        {/* Dynamic Grid for Input Fields */}
        <div
          className={`grid gap-3 
    ${locations.length === 1 ? 'grid-cols-1' : ''}
    ${locations.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : ''}
    ${locations.length === 3 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' : ''}
    ${locations.length >= 4 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3' : ''}
  `}>
          {locations.map((loc, index) => (
            <div key={index}>
              <div className="relative">
                <input
                  type="text"
                  value={loc}
                  onChange={e => handleChange(index, e.target.value)}
                  placeholder="Enter zip code"
                  className={`border text-[16px] font-inter leading-[26px] font-normal rounded-[13px] p-[15px] pr-8 w-full h-[56px] focus:outline-none 
                    ${
                      errors[index]
                        ? 'border-red-500 text-red-600 placeholder:text-red-400'
                        : 'border-[#E0E0E0] text-[#1B5C29] placeholder:text-[rgba(27,92,41,0.3)] focus:border-[#1B5C29]'
                    }`}
                    disabled={pinCodes.some(p => p.pincode === loc)}
                />
                {locations.length > 0 && (
                  <button
                    onClick={() => removeLocation(index)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 font-bold ">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {errors[index] && (
                <Typography className="text-red-500 text-sm mt-1">
                  {errors[index]}
                </Typography>
              )}
            </div>
          ))}
        </div>

        {error && <Typography className="text-red-500">{error}</Typography>}

        {/* Buttons */}
        <div className="flex flex-row gap-[10px] items-center justify-center sm:justify-end mt-4">
          <button
            onClick={addLocation}
            className="text-[16px] leading-[24px] font-medium font-inter text-black border border-[#00000033] py-[10px] px-[25px] bg-white rounded-[8px]">
            + Add Location
          </button>
          <button
            className={`text-[16px] leading-[24px] font-medium font-inter text-white py-[10px] px-[25px] bg-[#1B5C29] rounded-[8px] 
          ${
            shouldDisable
              ? 'opacity-40 cursor-not-allowed'
              : 'hover:bg-[#164a21]'
          }`}
            type="button"
            disabled={shouldDisable}
            onClick={handleSubmit}>
            Save Location
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ManageLocationModel;